import { useRef } from 'react';
import { RoutinePortraitProvider, BeingRoutinePortrait } from './BeingRoutinePortraits';
import MediaImage from './MediaImage';
import BeingPlanningEditor from './BeingPlanningEditor';
const NUMERIC_FIELDS = new Set([
  'createDateYear',
  'createDateDay',
  'destroyDateYear',
  'destroyDateDay',
  'level',
  'strength',
  'agility',
  'constitution',
  'intellect',
  'wisdom',
  'charisma',
  'maxHp',
  'currentHp',
  'maxPp',
  'currentPp',
  'maxStamina',
  'currentStamina',
  'hunger',
  'thirst',
  'intoxication',
  'lastVitalsAdvancedMinute',
  'bleeding',
  'poisoned',
  'other',
  'stunned',
  'disabled',
]);

const SPELL_NUMERIC_FIELDS = new Set(['quantity', 'level']);
const CURRENCY_NUMERIC_FIELDS = new Set(['quantity']);
const ROUTINE_NUMERIC_FIELDS = new Set(['startMinute', 'endMinute']);
const ROUTINE_STEP_NUMERIC_FIELDS = new Set(['startMinute', 'endMinute', 'checkInEveryMinutes']);
const uniqueValues = (values) => [...new Set(values.filter(Boolean))];
const routineTime = (minutes) => minutes == null || minutes === '' ? ''
  : `${String(Math.floor(Number(minutes) / 60) % 24).padStart(2, '0')}:${String(Number(minutes) % 60).padStart(2, '0')}`;
const routineMinutes = (time, isEnd = false) => {
  if (!time) return '';
  const [hours, minutes] = time.split(':').map(Number);
  return isEnd && hours === 0 && minutes === 0 ? 1440 : hours * 60 + minutes;
};

const SaveIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className="button-icon">
    <path
      d="M5 3h11l3 3v15H5V3zm2 2v4h8V5H7zm0 14h10v-8H7v8zm2-6h6v4H9v-4z"
      fill="currentColor"
    />
  </svg>
);

const CopyIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className="button-icon">
    <path
      d="M9 9h10v12H9V9zm-4-6h10v2H7v10H5V3zm4 2h8v2H9V5zm2 8v2h6v-2h-6zm0 4v2h6v-2h-6z"
      fill="currentColor"
    />
  </svg>
);

const PlusIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className="button-icon">
    <path d="M11 5h2v6h6v2h-6v6h-2v-6H5v-2h6V5z" fill="currentColor" />
  </svg>
);

const SparklesIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className="button-icon">
    <path
      d="M12 2l1.8 5.2L19 9l-5.2 1.8L12 16l-1.8-5.2L5 9l5.2-1.8L12 2zm7 12l.9 2.1L22 17l-2.1.9L19 20l-.9-2.1L16 17l2.1-.9L19 14zM5 14l1.1 2.9L9 18l-2.9 1.1L5 22l-1.1-2.9L1 18l2.9-1.1L5 14z"
      fill="currentColor"
    />
  </svg>
);

const TrashIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className="button-icon">
    <path
      d="M9 3h6l1 2h4v2H4V5h4l1-2zm1 6h2v8h-2V9zm4 0h2v8h-2V9zM7 9h2v8H7V9zm1 11h8a2 2 0 0 0 2-2V8H6v10a2 2 0 0 0 2 2z"
      fill="currentColor"
    />
  </svg>
);

function BeingEditor({
  title = 'Being Editor',
  copy = 'Edit the generated or loaded being directly, then save it.',
  value,
  onChange,
  onSave,
  onCopyJson,
  isSaving,
  saveMessage,
  getMasterTypeOptions,
  spellListOptions,
  spellUsageOptions,
  currencyOptions = [],
  placeOptions = [],
  onRemoveForm,
  onRemoveSpell,
  onGenerateItemSuggestions,
  onGenerateSuggestedItem,
  onGenerateAllItemSuggestions,
  onGenerateRoutines,
  onDeleteRelationship,
  relationshipTargetOptions = [],
  relateTypeOptions = [],
  isGeneratingItemSuggestions,
  isCreatingAllItemSuggestions,
  isGeneratingRoutines,
  aogYear = 21000,
  portrait = {},
  canGeneratePortrait = false,
  canSavePortrait = false,
  onGeneratePortrait,
  onSavePortrait,
}) {
  const routineKeyCounter = useRef(0);
  const being = value ?? {
    name: '',
    title: '',
    publicLabel: '',
    description: '',
    entityType: 'Being',
    createDateAge: '',
    createDateYear: '',
    createDateSeason: '',
    createDateDay: '',
    destroyDateAge: '',
    destroyDateYear: '',
    destroyDateSeason: '',
    destroyDateDay: '',
    level: '',
    beingType: '',
    strength: '',
    agility: '',
    constitution: '',
    intellect: '',
    wisdom: '',
    charisma: '',
    alignment: '',
    gender: '',
    heraldry: '',
    defaultForm: '',
    multipleForms: false,
    personality: '',
    objectivesShortTerm: '',
    objectivesLongTerm: '',
    maxHp: '',
    currentHp: '',
    maxPp: '',
    currentPp: '',
    maxStamina: '',
    currentStamina: '',
    hunger: 0,
    thirst: 0,
    intoxication: 0,
    lastVitalsAdvancedMinute: '',
    bleeding: 0,
    poisoned: 0,
    other: 0,
    otherDesc: '',
    stunned: 0,
    disabled: 0,
    ownedItems: [],
    currencies: [],
    relationships: [],
    itemSuggestions: [],
    routines: [],
    forms: [],
    spells: [],
  };

  const formOptions = uniqueValues([
    ...getMasterTypeOptions('beingType').map((option) => option.value),
    being.defaultForm,
    ...being.forms.map((form) => form.form),
  ]);
  const hasMultipleForms = (being.forms?.length ?? 0) > 1;

  const updateField = (field, nextValue) => {
    onChange({
      ...being,
      [field]: NUMERIC_FIELDS.has(field) && nextValue !== '' ? Number(nextValue) : nextValue,
    });
  };

  const updateFormRow = (index, field, nextValue) => {
    onChange({
      ...being,
      multipleForms: hasMultipleForms,
      forms: being.forms.map((form, currentIndex) =>
        currentIndex === index ? { ...form, [field]: nextValue } : form
      ),
    });
  };

  const addFormRow = () => {
    const nextForms = [...being.forms, { form: '', description: '', gmNotes: '' }];
    onChange({
      ...being,
      multipleForms: nextForms.length > 1,
      forms: nextForms,
    });
  };

  const updateSpellRow = (index, field, nextValue) => {
    onChange({
      ...being,
      spells: being.spells.map((spell, currentIndex) =>
        currentIndex === index
          ? {
              ...spell,
              [field]:
                SPELL_NUMERIC_FIELDS.has(field) && nextValue !== '' ? Number(nextValue) : nextValue,
            }
          : spell
      ),
    });
  };

  const addSpellRow = () => {
    onChange({
      ...being,
      spells: [
        ...being.spells,
        {
          idSpell: '',
          idSpellList: '',
          idSpellUsage: '',
          quantity: '',
          level: '',
          realm: '',
          acquiredVia: '',
          acquiredNotes: '',
        },
      ],
    });
  };

  const updateCurrencyRow = (index, field, nextValue) => {
    onChange({
      ...being,
      currencies: (being.currencies ?? []).map((currency, currentIndex) =>
        currentIndex === index
          ? {
              ...currency,
              [field]:
                CURRENCY_NUMERIC_FIELDS.has(field) && nextValue !== '' ? Number(nextValue) : nextValue,
            }
          : currency
      ),
    });
  };

  const addCurrencyRow = () => {
    onChange({
      ...being,
      currencies: [
        ...(being.currencies ?? []),
        {
          idCurrency: '',
          quantity: '',
        },
      ],
    });
  };

  const removeCurrencyRow = (index) => {
    onChange({
      ...being,
      currencies: (being.currencies ?? []).filter((_, currentIndex) => currentIndex !== index),
    });
  };

  const isItemSuggestionActionDisabled = Boolean(
    isGeneratingItemSuggestions || isCreatingAllItemSuggestions
  );

  const updateItemSuggestionRow = (index, field, nextValue) => {
    onChange({
      ...being,
      itemSuggestions: (being.itemSuggestions ?? []).map((suggestion, currentIndex) =>
        currentIndex === index
          ? {
              ...suggestion,
              [field]: field === 'level' && nextValue !== '' ? Number(nextValue) : nextValue,
            }
          : suggestion
      ),
    });
  };

  const addItemSuggestionRow = () => {
    onChange({
      ...being,
      itemSuggestions: [
        ...(being.itemSuggestions ?? []),
        { name: '', level: '', description: '' },
      ],
    });
  };

  const removeItemSuggestionRow = (index) => {
    onChange({
      ...being,
      itemSuggestions: (being.itemSuggestions ?? []).filter((_, currentIndex) => currentIndex !== index),
    });
  };

  const updateRoutineRow = (index, field, nextValue) => {
    onChange({
      ...being,
      routines: (being.routines ?? []).map((routine, currentIndex) =>
        currentIndex === index
          ? {
              ...routine,
              [field]:
                ROUTINE_NUMERIC_FIELDS.has(field) && nextValue !== '' ? Number(nextValue) : nextValue,
            }
          : routine
      ),
    });
  };

  const addRoutineRow = () => {
    const defaultStartMinute = 540;
    const defaultEndMinute = 1020;

    onChange({
      ...being,
      routines: [
        ...(being.routines ?? []),
        {
          _editorKey: `new-routine-${++routineKeyCounter.current}`,
          idBeing: being.idEntity ?? '',
          idPlace: placeOptions[0]?.value ?? '',
          startMinute: defaultStartMinute,
          endMinute: defaultEndMinute,
          sourceHash: 'editor',
          summary: '',
          steps: [],
        },
      ],
    });
  };

  const removeRoutineRow = (index) => {
    onChange({
      ...being,
      routines: (being.routines ?? []).filter((_, currentIndex) => currentIndex !== index),
    });
  };

  const updateRoutineStep = (routineIndex, stepIndex, field, nextValue) => {
    onChange({
      ...being,
      routines: (being.routines ?? []).map((routine, currentRoutineIndex) => {
        if (currentRoutineIndex !== routineIndex) {
          return routine;
        }

        return {
          ...routine,
          steps: (routine.steps ?? []).map((step, currentStepIndex) => {
            if (currentStepIndex !== stepIndex) {
              return step;
            }

            if (['targetX', 'targetY', 'targetZ'].includes(field)) {
              const targetField = field.replace('target', '').toLowerCase();
              return {
                ...step,
                targetXYZ: {
                  ...(step.targetXYZ ?? { x: '', y: '', z: '' }),
                  [targetField]: nextValue,
                },
              };
            }

            if (field === 'interruptible') {
              return { ...step, interruptible: Boolean(nextValue) };
            }
            if (field === 'noiseLevel') return { ...step, noise: nextValue === '' ? null : { level: Number(nextValue), description: step.noise?.description || '' } };
            if (field === 'noiseDescription') return { ...step, noise: { level: step.noise?.level ?? 2, description: nextValue } };

            return {
              ...step,
              [field]:
                ROUTINE_STEP_NUMERIC_FIELDS.has(field) && nextValue !== '' ? Number(nextValue) : nextValue,
            };
          }),
        };
      }),
    });
  };

  const addRoutineStep = (routineIndex) => {
    onChange({
      ...being,
      routines: (being.routines ?? []).map((routine, currentIndex) => {
        if (currentIndex !== routineIndex) {
          return routine;
        }

        return {
          ...routine,
          steps: [
            ...(routine.steps ?? []),
            {
              startMinute: routine.startMinute ?? 0,
              endMinute: routine.endMinute ?? 1440,
              action: '',
              targetXYZ: null,
              interruptible: true,
              checkInEveryMinutes: 10,
              notes: '',
            },
          ],
        };
      }),
    });
  };

  const removeRoutineStep = (routineIndex, stepIndex) => {
    onChange({
      ...being,
      routines: (being.routines ?? []).map((routine, currentIndex) =>
        currentIndex === routineIndex
          ? {
              ...routine,
              steps: (routine.steps ?? []).filter((_, currentStepIndex) => currentStepIndex !== stepIndex),
            }
          : routine
      ),
    });
  };

  const addRelationshipRow = () => {
    onChange({
      ...being,
      relationships: [
        ...(being.relationships ?? []),
        {
          isNew: true,
          direction: 'outgoing',
          relateType: '',
          otherEntityId: '',
          otherEntityName: '',
          otherEntityTitle: '',
          otherEntityType: '',
          score: 50,
        },
      ],
    });
  };

  const updateRelationshipRow = (index, field, nextValue) => {
    onChange({
      ...being,
      relationships: (being.relationships ?? []).map((relationship, currentIndex) => {
        if (currentIndex !== index) {
          return relationship;
        }

        if (field === 'otherEntityId') {
          const target = relationshipTargetOptions.find((option) => option.value === nextValue);
          return {
            ...relationship,
            otherEntityId: nextValue,
            otherEntityName: target?.name ?? '',
            otherEntityTitle: target?.title ?? target?.label ?? '',
            otherEntityType: target?.entityType ?? '',
          };
        }

        return { ...relationship, [field]: nextValue };
      }),
    });
  };

  const removeRelationshipRow = (index) => {
    onChange({
      ...being,
      relationships: (being.relationships ?? []).filter((_, currentIndex) => currentIndex !== index),
    });
  };

  return (
    <RoutinePortraitProvider being={being} onChange={onChange} aogYear={aogYear} primaryVersion={`${Boolean(portrait.isSaved)}:${Boolean(portrait.src)}`}>
    <section className="panel">
      <div className="panel-header-row">
        <div>
          <h2>{title}</h2>
          <p className="panel-copy">{copy}</p>
        </div>
        <div className="button-row compact">
          <button type="button" className="secondary-button button-with-icon" onClick={onCopyJson}>
            <CopyIcon />
            <span>JSON</span>
          </button>
          <button type="button" className="secondary-button button-with-icon" onClick={onSave} disabled={isSaving}>
            <SaveIcon />
            <span>{isSaving ? 'Saving...' : 'Save'}</span>
          </button>
        </div>
      </div>
      {saveMessage && <div className="panel-status">{saveMessage}</div>}

      <div className="editor-layout">
        <div className="being-editor-top-grid">
          <section className="editor-section">
            <h3>Entity</h3>
            <div className="editor-grid editor-grid-two">
              <label htmlFor="being-name" className="editor-field">
                <span className="field-label">Name</span>
                <input
                  id="being-name"
                  className="text-input editor-input"
                  value={being.name}
                  onChange={(event) => updateField('name', event.target.value)}
                />
              </label>
              <label htmlFor="being-title" className="editor-field">
                <span className="field-label">Title</span>
                <input
                  id="being-title"
                  className="text-input editor-input"
                  value={being.title}
                  onChange={(event) => updateField('title', event.target.value)}
                />
              </label>
              <label htmlFor="entity-type" className="editor-field">
                <span className="field-label">Entity Type</span>
                <select
                  id="entity-type"
                  className="select-input"
                  value={being.entityType ?? ''}
                  onChange={(event) => updateField('entityType', event.target.value)}
                >
                  {getMasterTypeOptions('entityType').map((option) => (
                    <option key={`entity-type-${option.value}`} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label htmlFor="being-level" className="editor-field">
                <span className="field-label">Level</span>
                <input
                  id="being-level"
                  className="text-input editor-input"
                  type="number"
                  value={being.level}
                  onChange={(event) => updateField('level', event.target.value)}
                />
              </label>
            </div>
            <label htmlFor="being-public-label" className="editor-field editor-field-spaced">
              <span className="field-label">Public label</span>
              <input id="being-public-label" className="text-input editor-input" maxLength={255}
                placeholder="a helmeted guard" value={being.publicLabel ?? ''}
                onChange={(event) => updateField('publicLabel', event.target.value)} />
              <span>What a stranger can recognize from appearance, without knowing their name or private occupation.</span>
            </label>
            <label htmlFor="being-description" className="editor-field editor-field-spaced">
              <span className="field-label">Description</span>
              <textarea
                id="being-description"
                className="text-area editor-textarea"
                value={being.description}
                onChange={(event) => updateField('description', event.target.value)}
              />
            </label>
          </section>

          <section className="editor-section being-portrait-section">
            <div className="being-portrait-copy">
              <h3>Image</h3>
              {portrait.error && <div className="panel-status error-status">{portrait.error}</div>}
            </div>
            <div className="being-portrait-panel">
              <div className="being-portrait-frame">
                {portrait.src ? (
                  <MediaImage size="display" src={portrait.src} alt="Being portrait" />
                ) : (
                  <span>{being.idEntity ? 'No image saved' : 'Save being first'}</span>
                )}
              </div>
              <div className="being-portrait-actions">
                <button
                  type="button"
                  className="secondary-button"
                  onClick={onGeneratePortrait}
                  disabled={!canGeneratePortrait || portrait.isGenerating || portrait.isSaving}
                >
                  {portrait.isGenerating ? 'Generating...' : 'Generate Image'}
                </button>
                <button
                  type="button"
                  className="secondary-button"
                  onClick={onSavePortrait}
                  disabled={!canSavePortrait || portrait.isGenerating || portrait.isSaving}
                >
                  {portrait.isSaving ? 'Saving...' : 'Save Image'}
                </button>
              </div>
            </div>
          </section>
        </div>

        <section className="editor-section">
          <h3>Timeline</h3>
          <div className="editor-grid editor-grid-four">
            <label htmlFor="create-age" className="editor-field">
              <span className="field-label">Create Age</span>
              <select
                id="create-age"
                className="select-input"
                value={being.createDateAge ?? ''}
                onChange={(event) => updateField('createDateAge', event.target.value)}
              >
                <option value="">Select...</option>
                {getMasterTypeOptions('age-multiple').map((option) => (
                  <option key={`create-age-${option.value}`} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label htmlFor="create-year" className="editor-field">
              <span className="field-label">Create Year</span>
              <input
                id="create-year"
                className="text-input editor-input"
                type="number"
                value={being.createDateYear}
                onChange={(event) => updateField('createDateYear', event.target.value)}
              />
            </label>
            <label htmlFor="create-season" className="editor-field">
              <span className="field-label">Create Season</span>
              <select
                id="create-season"
                className="select-input"
                value={being.createDateSeason ?? ''}
                onChange={(event) => updateField('createDateSeason', event.target.value)}
              >
                <option value="">Select...</option>
                {getMasterTypeOptions('season-multiple').map((option) => (
                  <option key={`create-season-${option.value}`} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label htmlFor="create-day" className="editor-field">
              <span className="field-label">Create Day</span>
              <input
                id="create-day"
                className="text-input editor-input"
                type="number"
                value={being.createDateDay}
                onChange={(event) => updateField('createDateDay', event.target.value)}
              />
            </label>
            <label htmlFor="destroy-age" className="editor-field">
              <span className="field-label">Destroy Age</span>
              <select
                id="destroy-age"
                className="select-input"
                value={being.destroyDateAge ?? ''}
                onChange={(event) => updateField('destroyDateAge', event.target.value)}
              >
                <option value="">Select...</option>
                {getMasterTypeOptions('age-multiple').map((option) => (
                  <option key={`destroy-age-${option.value}`} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label htmlFor="destroy-year" className="editor-field">
              <span className="field-label">Destroy Year</span>
              <input
                id="destroy-year"
                className="text-input editor-input"
                type="number"
                value={being.destroyDateYear}
                onChange={(event) => updateField('destroyDateYear', event.target.value)}
              />
            </label>
            <label htmlFor="destroy-season" className="editor-field">
              <span className="field-label">Destroy Season</span>
              <select
                id="destroy-season"
                className="select-input"
                value={being.destroyDateSeason ?? ''}
                onChange={(event) => updateField('destroyDateSeason', event.target.value)}
              >
                <option value="">Select...</option>
                {getMasterTypeOptions('season-multiple').map((option) => (
                  <option key={`destroy-season-${option.value}`} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label htmlFor="destroy-day" className="editor-field">
              <span className="field-label">Destroy Day</span>
              <input
                id="destroy-day"
                className="text-input editor-input"
                type="number"
                value={being.destroyDateDay}
                onChange={(event) => updateField('destroyDateDay', event.target.value)}
              />
            </label>
          </div>
        </section>

        <section className="editor-section">
          <h3>Being</h3>
          <div className="editor-grid editor-grid-three">
            <label htmlFor="being-type" className="editor-field">
              <span className="field-label">Being Type</span>
              <select
                id="being-type"
                className="select-input"
                value={being.beingType ?? ''}
                onChange={(event) => updateField('beingType', event.target.value)}
              >
                {getMasterTypeOptions('beingType').map((option) => (
                  <option key={`being-type-${option.value}`} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label htmlFor="alignment" className="editor-field">
              <span className="field-label">Alignment</span>
              <select
                id="alignment"
                className="select-input"
                value={being.alignment ?? ''}
                onChange={(event) => updateField('alignment', event.target.value)}
              >
                <option value="">Select...</option>
                {getMasterTypeOptions('alignment').map((option) => (
                  <option key={`alignment-${option.value}`} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label htmlFor="gender" className="editor-field">
              <span className="field-label">Gender</span>
              <select
                id="gender"
                className="select-input"
                value={being.gender ?? ''}
                onChange={(event) => updateField('gender', event.target.value)}
              >
                <option value="">Select...</option>
                {getMasterTypeOptions('gender').map((option) => (
                  <option key={`gender-${option.value}`} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label htmlFor="default-form" className="editor-field">
              <span className="field-label">Default Form</span>
              <select
                id="default-form"
                className="select-input"
                value={being.defaultForm ?? ''}
                onChange={(event) => updateField('defaultForm', event.target.value)}
              >
                <option value="">Select...</option>
                {formOptions.map((option) => (
                  <option key={`default-form-${option}`} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>
            <label htmlFor="strength" className="editor-field">
              <span className="field-label">Strength</span>
              <input
                id="strength"
                className="text-input editor-input"
                type="number"
                value={being.strength}
                onChange={(event) => updateField('strength', event.target.value)}
              />
            </label>
            <label htmlFor="agility" className="editor-field">
              <span className="field-label">Agility</span>
              <input
                id="agility"
                className="text-input editor-input"
                type="number"
                value={being.agility}
                onChange={(event) => updateField('agility', event.target.value)}
              />
            </label>
            <label htmlFor="constitution" className="editor-field">
              <span className="field-label">Constitution</span>
              <input
                id="constitution"
                className="text-input editor-input"
                type="number"
                value={being.constitution}
                onChange={(event) => updateField('constitution', event.target.value)}
              />
            </label>
            <label htmlFor="intellect" className="editor-field">
              <span className="field-label">Intellect</span>
              <input
                id="intellect"
                className="text-input editor-input"
                type="number"
                value={being.intellect}
                onChange={(event) => updateField('intellect', event.target.value)}
              />
            </label>
            <label htmlFor="wisdom" className="editor-field">
              <span className="field-label">Wisdom</span>
              <input
                id="wisdom"
                className="text-input editor-input"
                type="number"
                value={being.wisdom}
                onChange={(event) => updateField('wisdom', event.target.value)}
              />
            </label>
            <label htmlFor="charisma" className="editor-field">
              <span className="field-label">Charisma</span>
              <input
                id="charisma"
                className="text-input editor-input"
                type="number"
                value={being.charisma}
                onChange={(event) => updateField('charisma', event.target.value)}
              />
            </label>
          </div>
          <label htmlFor="heraldry" className="editor-field editor-field-spaced">
            <span className="field-label">Heraldry</span>
            <input
              id="heraldry"
              className="text-input editor-input editor-wide-input"
              value={being.heraldry}
              onChange={(event) => updateField('heraldry', event.target.value)}
            />
          </label>
          <label htmlFor="personality" className="editor-field">
            <span className="field-label">Personality</span>
            <textarea
              id="personality"
              className="text-area editor-textarea"
              value={being.personality}
              onChange={(event) => updateField('personality', event.target.value)}
            />
          </label>
          {!being.idEntity && <div className="editor-grid editor-grid-two">
            <label htmlFor="objectives-short" className="editor-field">
              <span className="field-label">Objectives Short Term</span>
              <textarea
                id="objectives-short"
                className="text-area editor-textarea"
                value={being.objectivesShortTerm}
                onChange={(event) => updateField('objectivesShortTerm', event.target.value)}
              />
            </label>
            <label htmlFor="objectives-long" className="editor-field">
              <span className="field-label">Objectives Long Term</span>
              <textarea
                id="objectives-long"
                className="text-area editor-textarea"
                value={being.objectivesLongTerm}
                onChange={(event) => updateField('objectivesLongTerm', event.target.value)}
              />
            </label>
          </div>}
        </section>

        {being.idEntity && <BeingPlanningEditor beingId={being.idEntity} initial={being.planning} placeOptions={placeOptions} routines={being.routines || []} />}

        <section className="editor-section">
          <h3>Health</h3>
          <div className="editor-grid editor-grid-four">
            <label htmlFor="being-max-hp" className="editor-field">
              <span className="field-label">Max HP</span>
              <input
                id="being-max-hp"
                className="text-input editor-input"
                type="number"
                value={being.maxHp}
                onChange={(event) => updateField('maxHp', event.target.value)}
              />
            </label>
            <label htmlFor="being-current-hp" className="editor-field">
              <span className="field-label">Current HP</span>
              <input
                id="being-current-hp"
                className="text-input editor-input"
                type="number"
                value={being.currentHp}
                onChange={(event) => updateField('currentHp', event.target.value)}
              />
            </label>
            <label htmlFor="being-max-pp" className="editor-field">
              <span className="field-label">Max PP</span>
              <input
                id="being-max-pp"
                className="text-input editor-input"
                type="number"
                value={being.maxPp}
                onChange={(event) => updateField('maxPp', event.target.value)}
              />
            </label>
            <label htmlFor="being-current-pp" className="editor-field">
              <span className="field-label">Current PP</span>
              <input
                id="being-current-pp"
                className="text-input editor-input"
                type="number"
                value={being.currentPp}
                onChange={(event) => updateField('currentPp', event.target.value)}
              />
            </label>
            <label htmlFor="being-max-stamina" className="editor-field">
              <span className="field-label">Max Stamina</span>
              <input
                id="being-max-stamina"
                className="text-input editor-input"
                type="number"
                value={being.maxStamina}
                onChange={(event) => updateField('maxStamina', event.target.value)}
              />
            </label>
            <label htmlFor="being-current-stamina" className="editor-field">
              <span className="field-label">Current Stamina</span>
              <input
                id="being-current-stamina"
                className="text-input editor-input"
                type="number"
                value={being.currentStamina}
                onChange={(event) => updateField('currentStamina', event.target.value)}
              />
            </label>
            <label htmlFor="being-hunger" className="editor-field">
              <span className="field-label">Hunger</span>
              <input
                id="being-hunger"
                className="text-input editor-input"
                type="number"
                min="0"
                max="100"
                value={being.hunger}
                onChange={(event) => updateField('hunger', event.target.value)}
              />
            </label>
            <label htmlFor="being-thirst" className="editor-field">
              <span className="field-label">Thirst</span>
              <input
                id="being-thirst"
                className="text-input editor-input"
                type="number"
                min="0"
                max="100"
                value={being.thirst}
                onChange={(event) => updateField('thirst', event.target.value)}
              />
            </label>
            <label htmlFor="being-intoxication" className="editor-field">
              <span className="field-label">Intoxication</span>
              <input
                id="being-intoxication"
                className="text-input editor-input"
                type="number"
                min="0"
                max="100"
                value={being.intoxication}
                onChange={(event) => updateField('intoxication', event.target.value)}
              />
            </label>
            <label htmlFor="being-last-vitals-advanced-minute" className="editor-field">
              <span className="field-label">Vitals Minute</span>
              <input
                id="being-last-vitals-advanced-minute"
                className="text-input editor-input"
                type="number"
                value={being.lastVitalsAdvancedMinute}
                onChange={(event) => updateField('lastVitalsAdvancedMinute', event.target.value)}
              />
            </label>
            <label htmlFor="being-bleeding" className="editor-field">
              <span className="field-label">Bleeding</span>
              <input
                id="being-bleeding"
                className="text-input editor-input"
                type="number"
                value={being.bleeding}
                onChange={(event) => updateField('bleeding', event.target.value)}
              />
            </label>
            <label htmlFor="being-poisoned" className="editor-field">
              <span className="field-label">Poisoned</span>
              <input
                id="being-poisoned"
                className="text-input editor-input"
                type="number"
                value={being.poisoned}
                onChange={(event) => updateField('poisoned', event.target.value)}
              />
            </label>
            <label htmlFor="being-other" className="editor-field">
              <span className="field-label">Other</span>
              <input
                id="being-other"
                className="text-input editor-input"
                type="number"
                value={being.other}
                onChange={(event) => updateField('other', event.target.value)}
              />
            </label>
            <label htmlFor="being-stunned" className="editor-field">
              <span className="field-label">Stunned</span>
              <input
                id="being-stunned"
                className="text-input editor-input"
                type="number"
                value={being.stunned}
                onChange={(event) => updateField('stunned', event.target.value)}
              />
            </label>
            <label htmlFor="being-disabled" className="editor-field">
              <span className="field-label">Disabled</span>
              <input
                id="being-disabled"
                className="text-input editor-input"
                type="number"
                value={being.disabled}
                onChange={(event) => updateField('disabled', event.target.value)}
              />
            </label>
          </div>
          <label htmlFor="being-other-desc" className="editor-field editor-field-spaced">
            <span className="field-label">Other Description</span>
            <input
              id="being-other-desc"
              className="text-input editor-input editor-wide-input"
              value={being.otherDesc}
              onChange={(event) => updateField('otherDesc', event.target.value)}
            />
          </label>
        </section>

        <section className="editor-section">
          <h3>Owned Items</h3>
          {being.ownedItems.length > 0 ? (
            <div className="editor-table">
              <div className="editor-table-row editor-table-head editor-grid-two">
                <span>Name</span>
                <span>Title</span>
              </div>
              {being.ownedItems.map((item) => (
                <div key={`owned-item-${item.idEntity}`} className="editor-table-row editor-grid-two">
                  <span>{item.name || '(Unnamed Item)'}</span>
                  <span>{item.title || ''}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-grid">No owned items.</div>
          )}
        </section>

        <section className="editor-section">
          <div className="section-header">
            <h3>Currency</h3>
            <button type="button" className="secondary-button button-with-icon" onClick={addCurrencyRow}>
              <PlusIcon />
              <span>Add Currency</span>
            </button>
          </div>
          <div className="editor-table">
            <div className="editor-table-row editor-table-head being-currency-grid">
              <span>Currency</span>
              <span>Quantity</span>
              <span className="table-action-head" title="Remove">
                <TrashIcon />
              </span>
            </div>
            {(being.currencies ?? []).map((currency, index) => (
              <div key={`being-currency-${currency.idCurrency || index}`} className="editor-table-row being-currency-grid">
                <select
                  className="select-input"
                  value={currency.idCurrency ?? ''}
                  onChange={(event) => updateCurrencyRow(index, 'idCurrency', event.target.value)}
                >
                  <option value="">Select...</option>
                  {currencyOptions.map((option) => (
                    <option key={`currency-option-${option.value}`} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <input
                  className="text-input editor-input"
                  type="number"
                  value={currency.quantity ?? ''}
                  onChange={(event) => updateCurrencyRow(index, 'quantity', event.target.value)}
                />
                <button
                  type="button"
                  className="icon-button danger-button"
                  aria-label="Remove currency"
                  onClick={() => removeCurrencyRow(index)}
                >
                  <TrashIcon />
                </button>
              </div>
            ))}
            {(being.currencies?.length ?? 0) === 0 && <div className="empty-grid">No currency.</div>}
          </div>
        </section>

        <section className="editor-section">
          <div className="section-header">
            <h3>Suggested Items</h3>
            <div className="button-row compact">
              <button
                type="button"
                className="secondary-button button-with-icon"
                onClick={onGenerateItemSuggestions}
                disabled={isItemSuggestionActionDisabled}
              >
                <SparklesIcon />
                <span>{isGeneratingItemSuggestions ? 'Refreshing...' : 'Refresh Suggestions'}</span>
              </button>
              <button
                type="button"
                className="secondary-button button-with-icon"
                onClick={() => onGenerateAllItemSuggestions?.(being.itemSuggestions ?? [])}
                disabled={isItemSuggestionActionDisabled || !being.itemSuggestions?.length}
              >
                <PlusIcon />
                <span>{isCreatingAllItemSuggestions ? 'Creating...' : 'Create All'}</span>
              </button>
              <button
                type="button"
                className="secondary-button button-with-icon"
                onClick={addItemSuggestionRow}
              >
                <PlusIcon />
                <span>Add Suggestion</span>
              </button>
            </div>
          </div>
          <div className="editor-table">
            <div className="editor-table-row editor-table-head being-item-suggestions-grid">
              <span>Name</span>
              <span>Level</span>
              <span>Description</span>
              <span className="table-action-head" title="Create">
                <PlusIcon />
              </span>
              <span className="table-action-head" title="Remove">
                <TrashIcon />
              </span>
            </div>
            {(being.itemSuggestions ?? []).map((suggestion, index) => (
              <div key={`item-suggestion-${index}`} className="editor-table-row being-item-suggestions-grid">
                <input
                  className="text-input editor-input"
                  value={suggestion.name ?? ''}
                  onChange={(event) => updateItemSuggestionRow(index, 'name', event.target.value)}
                />
                <input
                  className="text-input editor-input"
                  type="number"
                  value={suggestion.level ?? ''}
                  onChange={(event) => updateItemSuggestionRow(index, 'level', event.target.value)}
                />
                <textarea
                  className="text-area grid-textarea"
                  value={suggestion.description ?? ''}
                  onChange={(event) => updateItemSuggestionRow(index, 'description', event.target.value)}
                />
                <button
                  type="button"
                  className="icon-button"
                  title={suggestion.instruction ? `Teaches ${suggestion.instruction.list} through level ${suggestion.instruction.level} (${suggestion.instruction.acquiredVia})` : 'Create item'}
                  aria-label="Create item"
                  onClick={() => onGenerateSuggestedItem?.(suggestion, index)}
                  disabled={isItemSuggestionActionDisabled}
                >
                  <PlusIcon />
                </button>
                <button
                  type="button"
                  className="icon-button danger-button"
                  aria-label="Remove suggestion"
                  onClick={() => removeItemSuggestionRow(index)}
                >
                  <TrashIcon />
                </button>
              </div>
            ))}
            {(being.itemSuggestions?.length ?? 0) === 0 && <div className="empty-grid">No suggested items yet.</div>}
          </div>
        </section>

        <section className="editor-section">
          <div className="section-header">
            <div>
              <h3>Routines</h3>
              <span className="panel-copy">{(being.routines?.length ?? 0)} routine row(s)</span>
            </div>
            <div className="button-row compact">
              <button
                type="button"
                className="secondary-button button-with-icon"
                onClick={onGenerateRoutines}
                disabled={isGeneratingRoutines}
              >
                <SparklesIcon />
                <span>{isGeneratingRoutines ? 'Generating...' : 'Generate Routines'}</span>
              </button>
              <button type="button" className="secondary-button button-with-icon" onClick={addRoutineRow}>
                <PlusIcon />
                <span>Add Routine</span>
              </button>
            </div>
          </div>
          <div className="being-routine-list">
            {(being.routines ?? []).map((routine, routineIndex) => (
              <div key={`being-routine-${routine._editorKey || routine.idBeingRoutine || routineIndex}`} className="being-routine-card">
                <div className="editor-table-row being-routine-grid">
                  <label className="editor-field">
                    <span className="field-label">Place</span>
                    <select
                      className="select-input"
                      value={routine.idPlace ?? ''}
                      onChange={(event) => updateRoutineRow(routineIndex, 'idPlace', event.target.value)}
                    >
                      <option value="">Select...</option>
                      {placeOptions.map((option) => (
                        <option key={`routine-place-${option.value}`} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="editor-field">
                    <span className="field-label">Start</span>
                    <input
                      className="text-input editor-input"
                      type="time"
                      step="60"
                      value={routineTime(routine.startMinute)}
                      onChange={(event) => updateRoutineRow(routineIndex, 'startMinute', routineMinutes(event.target.value))}
                    />
                  </label>
                  <label className="editor-field">
                    <span className="field-label">End</span>
                    <input
                      className="text-input editor-input"
                      type="time"
                      step="60"
                      title="Midnight ends the day (24:00)."
                      value={routineTime(routine.endMinute)}
                      onChange={(event) => updateRoutineRow(routineIndex, 'endMinute', routineMinutes(event.target.value, true))}
                    />
                  </label>
                  <label className="editor-field">
                    <span className="field-label">Source Hash</span>
                    <input
                      className="text-input editor-input"
                      value={routine.sourceHash ?? ''}
                      onChange={(event) => updateRoutineRow(routineIndex, 'sourceHash', event.target.value)}
                    />
                  </label>
                  <button
                    type="button"
                    className="icon-button danger-button routine-remove-button"
                    aria-label="Remove routine"
                    onClick={() => removeRoutineRow(routineIndex)}
                  >
                    <TrashIcon />
                  </button>
                </div>
                <label className="editor-field editor-field-spaced">
                  <span className="field-label">Summary</span>
                  <textarea
                    className="text-area grid-textarea"
                    value={routine.summary ?? ''}
                    onChange={(event) => updateRoutineRow(routineIndex, 'summary', event.target.value)}
                  />
                </label>
                <BeingRoutinePortrait routine={routine} index={routineIndex} beingId={being.idEntity} />
                <div className="routine-step-header">
                  <h4>Steps</h4>
                  <button
                    type="button"
                    className="secondary-button button-with-icon"
                    onClick={() => addRoutineStep(routineIndex)}
                  >
                    <PlusIcon />
                    <span>Add Step</span>
                  </button>
                </div>
                <div className="editor-table">
                  <div className="editor-table-row editor-table-head being-routine-steps-grid">
                    <span>Start</span>
                    <span>End</span>
                    <span>Action</span>
                    <span>Target X</span>
                    <span>Target Y</span>
                    <span>Target Z</span>
                    <span>Check In</span>
                    <span>Interrupt</span>
                    <span>Notes</span>
                    <span className="table-action-head" title="Remove">
                      <TrashIcon />
                    </span>
                  </div>
                  {(routine.steps ?? []).map((step, stepIndex) => (
                    <div
                      key={`being-routine-${routineIndex}-step-${stepIndex}`}
                      className="editor-table-row being-routine-steps-grid"
                    >
                      <input
                        className="text-input editor-input"
                        type="time"
                        step="60"
                        aria-label={`Routine ${routineIndex + 1} step ${stepIndex + 1} start`}
                        value={routineTime(step.startMinute)}
                        onChange={(event) => updateRoutineStep(routineIndex, stepIndex, 'startMinute', routineMinutes(event.target.value))}
                      />
                      <input
                        className="text-input editor-input"
                        type="time"
                        step="60"
                        aria-label={`Routine ${routineIndex + 1} step ${stepIndex + 1} end`}
                        title="Midnight ends the day (24:00)."
                        value={routineTime(step.endMinute)}
                        onChange={(event) => updateRoutineStep(routineIndex, stepIndex, 'endMinute', routineMinutes(event.target.value, true))}
                      />
                      <textarea
                        className="text-area grid-textarea"
                        value={step.action ?? ''}
                        onChange={(event) => updateRoutineStep(routineIndex, stepIndex, 'action', event.target.value)}
                      />
                      <input
                        className="text-input editor-input"
                        type="number"
                        value={step.targetXYZ?.x ?? ''}
                        onChange={(event) => updateRoutineStep(routineIndex, stepIndex, 'targetX', event.target.value)}
                      />
                      <input
                        className="text-input editor-input"
                        type="number"
                        value={step.targetXYZ?.y ?? ''}
                        onChange={(event) => updateRoutineStep(routineIndex, stepIndex, 'targetY', event.target.value)}
                      />
                      <input
                        className="text-input editor-input"
                        type="number"
                        value={step.targetXYZ?.z ?? ''}
                        onChange={(event) => updateRoutineStep(routineIndex, stepIndex, 'targetZ', event.target.value)}
                      />
                      <input
                        className="text-input editor-input"
                        type="number"
                        min="1"
                        value={step.checkInEveryMinutes ?? ''}
                        onChange={(event) =>
                          updateRoutineStep(routineIndex, stepIndex, 'checkInEveryMinutes', event.target.value)
                        }
                      />
                      <label className="routine-interrupt-toggle">
                        <input
                          type="checkbox"
                          checked={Boolean(step.interruptible)}
                          onChange={(event) =>
                            updateRoutineStep(routineIndex, stepIndex, 'interruptible', event.target.checked)
                          }
                        />
                        <span>Yes</span>
                      </label>
                      <div><textarea
                        className="text-area grid-textarea"
                        value={step.notes ?? ''}
                        onChange={(event) => updateRoutineStep(routineIndex, stepIndex, 'notes', event.target.value)}
                      />
                      <label>Activity sound<select aria-label={`Routine sound ${routineIndex} ${stepIndex}`} value={step.noise?.level ?? ''}
                        onChange={event => updateRoutineStep(routineIndex, stepIndex, 'noiseLevel', event.target.value)}>
                        <option value="">Unassessed</option>{['Silent', 'Quiet', 'Ordinary', 'Loud', 'Very loud'].map((text, level) => <option key={level} value={level}>{text}</option>)}
                      </select><input aria-label={`Routine sound description ${routineIndex} ${stepIndex}`} value={step.noise?.description || ''} maxLength={300}
                        onChange={event => updateRoutineStep(routineIndex, stepIndex, 'noiseDescription', event.target.value)} /></label></div>
                      <button
                        type="button"
                        className="icon-button danger-button"
                        aria-label="Remove routine step"
                        onClick={() => removeRoutineStep(routineIndex, stepIndex)}
                      >
                        <TrashIcon />
                      </button>
                    </div>
                  ))}
                  {(routine.steps?.length ?? 0) === 0 && <div className="empty-grid">No routine steps.</div>}
                </div>
              </div>
            ))}
            {(being.routines?.length ?? 0) === 0 && <div className="empty-grid">No routines yet.</div>}
          </div>
        </section>

        <section className="editor-section">
          <div className="section-header">
            <div>
              <h3>Forms</h3>
              <span className="panel-copy">Multiple Forms: {hasMultipleForms ? 'Yes' : 'No'}</span>
            </div>
            <button type="button" className="secondary-button button-with-icon" onClick={addFormRow}>
              <PlusIcon />
              <span>Add Form</span>
            </button>
          </div>
          <div className="editor-table">
            <div className="editor-table-row editor-table-head forms-grid">
              <span>Form</span>
              <span>Description</span>
              <span>GM Notes</span>
              <span className="table-action-head" title="Remove">
                <TrashIcon />
              </span>
            </div>
            {being.forms.map((form, index) => (
              <div key={`form-${index}`} className="editor-table-row forms-grid">
                <input
                  className="text-input editor-input combobox-input"
                  list="being-form-options"
                  value={form.form}
                  onChange={(event) => updateFormRow(index, 'form', event.target.value)}
                  placeholder="Select or type..."
                />
                <textarea
                  className="text-area grid-textarea"
                  value={form.description}
                  onChange={(event) => updateFormRow(index, 'description', event.target.value)}
                />
                <textarea
                  className="text-area grid-textarea"
                  value={form.gmNotes}
                  onChange={(event) => updateFormRow(index, 'gmNotes', event.target.value)}
                />
                <button
                  type="button"
                  className="icon-button danger-button"
                  aria-label="Remove form"
                  onClick={() => onRemoveForm(index, form)}
                >
                  <TrashIcon />
                </button>
              </div>
            ))}
            {being.forms.length === 0 && <div className="empty-grid">No forms yet.</div>}
          </div>
          <datalist id="being-form-options">
            {formOptions.map((optionValue) => (
              <option key={`form-option-${optionValue}`} value={optionValue} />
            ))}
          </datalist>
        </section>

        <section className="editor-section">
          <div className="section-header">
            <h3>Spells</h3>
            <button type="button" className="secondary-button button-with-icon" onClick={addSpellRow}>
              <PlusIcon />
              <span>Add Spell</span>
            </button>
          </div>
          <div className="editor-table">
            <div className="editor-table-row editor-table-head spells-grid">
              <span>Spell List</span>
              <span>Spell Usage</span>
              <span>Quantity</span>
              <span title="Highest learned or granted level; ordinary casting can use less.">Known Level</span>
              <span>Realm</span>
              <span>Acquired Via</span>
              <span>Notes</span>
              <span className="table-action-head" title="Remove">
                <TrashIcon />
              </span>
            </div>
            {being.spells.map((spell, index) => (
              <div key={`spell-${index}`} className="editor-table-row spells-grid">
                <select
                  className="select-input"
                  value={spell.idSpellList}
                  onChange={(event) => updateSpellRow(index, 'idSpellList', event.target.value)}
                >
                  <option value="">Select...</option>
                  {spellListOptions.map((option) => (
                    <option key={`spell-list-${option.value}`} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <select
                  className="select-input"
                  value={spell.idSpellUsage}
                  onChange={(event) => updateSpellRow(index, 'idSpellUsage', event.target.value)}
                >
                  <option value="">Select...</option>
                  {spellUsageOptions.map((option) => (
                    <option key={`spell-usage-${option.value}`} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <input
                  className="text-input editor-input"
                  type="number"
                  value={spell.quantity}
                  onChange={(event) => updateSpellRow(index, 'quantity', event.target.value)}
                />
                <input
                  className="text-input editor-input"
                  type="number"
                  value={spell.level}
                  onChange={(event) => updateSpellRow(index, 'level', event.target.value)}
                />
                <select
                  className="select-input"
                  value={spell.realm}
                  onChange={(event) => updateSpellRow(index, 'realm', event.target.value)}
                >
                  <option value="">Select...</option>
                  {getMasterTypeOptions('realm').map((option) => (
                    <option key={`spell-realm-${option.value}`} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <select
                  className="select-input"
                  value={spell.acquiredVia}
                  onChange={(event) => updateSpellRow(index, 'acquiredVia', event.target.value)}
                >
                  <option value="">Select...</option>
                  {getMasterTypeOptions('acquiredVia').map((option) => (
                    <option key={`spell-acquired-${option.value}`} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <textarea
                  className="text-area grid-textarea"
                  value={spell.acquiredNotes}
                  onChange={(event) => updateSpellRow(index, 'acquiredNotes', event.target.value)}
                />
                <button
                  type="button"
                  className="icon-button danger-button"
                  aria-label="Remove spell"
                  onClick={() => onRemoveSpell(index, spell)}
                >
                  <TrashIcon />
                </button>
              </div>
            ))}
            {being.spells.length === 0 && <div className="empty-grid">No spells yet.</div>}
          </div>
        </section>

        <section className="editor-section">
          <div className="section-header">
            <h3>Relationships</h3>
            <button type="button" className="secondary-button button-with-icon" onClick={addRelationshipRow}>
              <PlusIcon />
              <span>Add Relationship</span>
            </button>
          </div>
          {(being.relationships ?? []).length > 0 ? (
            <div className="editor-table">
              <div className="editor-table-row editor-table-head being-relationships-grid">
                <span>Relate Type</span>
                <span>Other Entity</span>
                <span>Entity Type</span>
                <span>Score</span>
                <span className="table-action-head" title="Delete">
                  <TrashIcon />
                </span>
              </div>
              {(being.relationships ?? []).map((relationship, index) => (
                <div key={`being-relationship-${relationship.otherEntityId || index}`} className="editor-table-row being-relationships-grid">
                  {relationship.isNew ? (
                    <select
                      className="select-input"
                      value={relationship.relateType ?? ''}
                      onChange={(event) => updateRelationshipRow(index, 'relateType', event.target.value)}
                    >
                      <option value="">Select...</option>
                      {relateTypeOptions.map((option) => (
                        <option key={`relationship-type-${option.value}`} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span>{relationship.relateType || ''}</span>
                  )}
                  {relationship.isNew ? (
                    <select
                      className="select-input"
                      value={relationship.otherEntityId ?? ''}
                      onChange={(event) => updateRelationshipRow(index, 'otherEntityId', event.target.value)}
                    >
                      <option value="">Select...</option>
                      {relationshipTargetOptions.map((option) => (
                        <option key={`relationship-target-${option.value}`} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span>{relationship.otherEntityTitle || relationship.otherEntityName || relationship.otherEntityId || ''}</span>
                  )}
                  <span>{relationship.otherEntityType || ''}</span>
                  {relationship.isNew ? (
                    <input
                      className="text-input"
                      type="number"
                      min="0"
                      max="100"
                      step="1"
                      value={relationship.score ?? 50}
                      onChange={(event) => updateRelationshipRow(index, 'score', event.target.value)}
                    />
                  ) : (
                    <span>
                      {Number.isFinite(Number(relationship.score)) ? Number(relationship.score).toFixed(0) : '50'}
                      {relationship.demeanor ? ` - ${relationship.demeanor}` : ''}
                    </span>
                  )}
                  <button
                    type="button"
                    className="icon-button danger-button"
                    aria-label="Delete relationship"
                    title="Delete relationship"
                    onClick={() => {
                      if (relationship.isNew) {
                        removeRelationshipRow(index);
                        return;
                      }

                      onDeleteRelationship?.(relationship, index);
                    }}
                    disabled={!relationship.isNew && !onDeleteRelationship}
                  >
                    <TrashIcon />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-grid">No being or place relationships.</div>
          )}
        </section>
      </div>
    </section>
    </RoutinePortraitProvider>
  );
}

export default BeingEditor;
